import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Boot messages that appear during startup
const BOOT_MESSAGES = [
  'Laster systemfiler...',
  'Initialiserer AI-moduler...',
  'Kobler til nettverk...',
  'Laster brukerinnstillinger...',
  'Starter Autonomous 95...'
];

export default function Intro({ onComplete }) {
  const [progress, setProgress] = useState(0);
  const [logoProgress, setLogoProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [loadingDots, setLoadingDots] = useState('');
  const [currentMessage, setCurrentMessage] = useState(0);
  const [showSkipHint, setShowSkipHint] = useState(false);

  // Progress animation
  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + Math.random() * 4 + 1;
      });
    }, 60);

    return () => clearInterval(progressInterval);
  }, []);

  // Logo reveal animation
  useEffect(() => {
    const logoInterval = setInterval(() => {
      setLogoProgress(prev => {
        if (prev >= 100) {
          clearInterval(logoInterval);
          return 100;
        }
        return prev + 2;
      });
    }, 30);

    return () => clearInterval(logoInterval);
  }, []);

  // Loading dots animation
  useEffect(() => {
    const dotsInterval = setInterval(() => {
      setLoadingDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 400);

    return () => clearInterval(dotsInterval);
  }, []);

  // Cycle through boot messages
  useEffect(() => {
    const messageInterval = setInterval(() => {
      setCurrentMessage(prev => (prev + 1) % BOOT_MESSAGES.length);
    }, 800);

    return () => clearInterval(messageInterval);
  }, []);

  // Show skip hint after 1 second
  useEffect(() => {
    const timer = setTimeout(() => setShowSkipHint(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Complete animation
  useEffect(() => {
    if (progress >= 100 && logoProgress >= 100) {
      setTimeout(() => setIsComplete(true), 300);
      setTimeout(() => onComplete?.(), 1000);
    }
  }, [progress, logoProgress, onComplete]);

  // Skip intro on click
  const handleSkip = () => {
    setProgress(100);
    setLogoProgress(100);
    setIsComplete(true);
    setTimeout(() => onComplete?.(), 200);
  };

  return (
    <motion.div 
      className="intro-screen"
      onClick={handleSkip}
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'linear-gradient(135deg, #000000 0%, #000033 50%, #000066 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        zIndex: 10000,
        overflow: 'hidden'
      }}
    >
      {/* Background stars */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        {Array.from({ length: 50 }).map((_, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.8, 0] }}
            transition={{
              duration: Math.random() * 3 + 2,
              delay: Math.random() * 2,
              repeat: Infinity
            }}
            style={{
              position: 'absolute',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 2 + 1}px`,
              height: `${Math.random() * 2 + 1}px`,
              background: 'white',
              borderRadius: '50%'
            }}
          />
        ))}
      </div>

      {/* Main content */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          zIndex: 1
        }}
      >
        {/* Windows Logo */}
        <motion.div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            width: '100px',
            height: '100px',
            gap: '6px',
            marginBottom: '32px'
          }}
          animate={{
            rotateY: [0, 360],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "linear"
          }}
        >
          {['#ff0000', '#00ff00', '#0000ff', '#ffff00'].map((color, i) => (
            <motion.div
              key={i}
              initial={{ scale: 0, rotate: -180 }}
              animate={{ 
                scale: logoProgress > i * 25 ? 1 : 0,
                rotate: 0,
                boxShadow: isComplete 
                  ? `0 0 20px ${color}, 0 0 40px ${color}40`
                  : `0 0 ${10 + (logoProgress / 10)}px ${color}80`
              }}
              transition={{ 
                duration: 0.5, 
                delay: i * 0.15,
                type: 'spring',
                stiffness: 200
              }}
              style={{
                background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
                borderRadius: '4px'
              }}
            />
          ))}
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          style={{
            fontFamily: "'Segoe UI', Tahoma, sans-serif",
            fontSize: '36px',
            fontWeight: 'bold',
            color: 'white',
            marginBottom: '8px',
            textShadow: '0 0 20px rgba(255,255,255,0.5), 0 2px 4px rgba(0,0,0,0.5)'
          }}
        >
          Autonomous
          <span style={{ 
            fontSize: '20px', 
            fontWeight: '300',
            marginLeft: '8px',
            opacity: 0.8 
          }}>
            95
          </span>
        </motion.h1>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          transition={{ delay: 0.5 }}
          style={{
            fontFamily: "'Segoe UI', sans-serif",
            fontSize: '14px',
            color: 'white',
            marginBottom: '40px'
          }}
        >
          Vi bygger AI som fungerer.
        </motion.p>

        {/* Progress bar container */}
        <div style={{
          width: '320px',
          marginBottom: '16px'
        }}>
          {/* Progress bar */}
          <div style={{
            height: '22px',
            background: '#000',
            border: '2px solid',
            borderColor: '#404040 #ffffff #ffffff #404040',
            padding: '3px',
            marginBottom: '8px'
          }}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(progress, 100)}%` }}
              transition={{ duration: 0.3 }}
              style={{
                height: '100%',
                background: 'linear-gradient(90deg, #000080 0%, #0000ff 50%, #000080 100%)',
                backgroundSize: '20px 100%',
                animation: 'progress-stripe 0.5s linear infinite'
              }}
            />
          </div>

          {/* Boot message */}
          <motion.div
            key={currentMessage}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.8 }}
            style={{
              fontFamily: "'Courier New', monospace",
              fontSize: '11px',
              color: '#00ff00',
              textAlign: 'center',
              height: '16px'
            }}
          >
            {!isComplete && BOOT_MESSAGES[currentMessage]}
          </motion.div>
        </div>

        {/* Status text */}
        <motion.div
          style={{
            fontFamily: "'Segoe UI', sans-serif",
            fontSize: '13px',
            color: 'white',
            textAlign: 'center'
          }}
        >
          {isComplete ? (
            <motion.span
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{ fontWeight: '600', color: '#00ff00' }}
            >
              ✓ Velkommen!
            </motion.span>
          ) : (
            <span style={{ opacity: 0.8 }}>
              Starter Autonomous 95{loadingDots}
            </span>
          )}
        </motion.div>
      </motion.div>

      {/* Skip hint */}
      <AnimatePresence>
        {showSkipHint && !isComplete && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'absolute',
              bottom: '32px',
              fontFamily: "'Segoe UI', sans-serif",
              fontSize: '12px',
              color: 'white',
              background: 'rgba(0,0,0,0.3)',
              padding: '8px 16px',
              borderRadius: '4px',
              backdropFilter: 'blur(4px)'
            }}
          >
            Klikk hvor som helst for å hoppe over
          </motion.div>
        )}
      </AnimatePresence>

      {/* CSS for progress stripe animation */}
      <style>{`
        @keyframes progress-stripe {
          0% { background-position: 0 0; }
          100% { background-position: 20px 0; }
        }
      `}</style>
    </motion.div>
  );
}
